/**
 * A list of runs, newest first, a page at a time: a scenario type's bulk runs, a dataset's runs and
 * the simulator's kept runs alike. Each list builds its own query over its own endpoint.
 */

import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithInfiniteQuery } from 'jotai-tanstack-query';
import { getApiErrorMessage } from '@/lib/utils';

interface RunPage<T> {
	runs: T[];
	total: number;
}

const PAGE_SIZE = 20;

/** Query roots of a scenario type's bulk runs and a dataset's runs, which the socket refreshes as runs move. */
export const BULK_RUNS_QUERY_ROOT = 'bulk-runs';
export const DATASET_RUNS_QUERY_ROOT = 'dataset-runs';

/** The API pages by offset: the next page starts where the rows loaded so far end. */
export const runPagesAtom = <T>(queryKey: string[], fetchPage: (offset: number, limit: number) => Promise<RunPage<T>>) =>
	atomWithInfiniteQuery(() => ({
		queryKey,
		queryFn: ({ pageParam }: { pageParam: number }) => fetchPage(pageParam, PAGE_SIZE),
		initialPageParam: 0,
		getNextPageParam: (lastPage: RunPage<T>, pages: RunPage<T>[]) => {
			const loaded = pages.reduce((sum, page) => sum + page.runs.length, 0);
			return loaded < lastPage.total ? loaded : undefined;
		}
	}));

export interface RunPages<T> {
	runs: T[];
	isLoading: boolean;
	error: string | null;
	hasMore: boolean;
	loadingMore: boolean;
	loadMore: () => void;
}

/** Bulk runs and dataset runs are told apart by their runId. */
export const runIdOf = (run: { runId: string }): string => run.runId;

/** A run started between two page reads shifts every offset by one, so a row can arrive twice; it shows once. */
export const useRunPages = <T>(
	queryAtom: ReturnType<typeof runPagesAtom<T>>,
	idOf: (run: T) => string,
	failure: string
): RunPages<T> => {
	const { data, error, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useAtomValue(queryAtom);

	const runs = useMemo(() => {
		const all = data?.pages.flatMap((page) => page.runs) ?? [];
		return all.filter((run, index) => all.findIndex((other) => idOf(other) === idOf(run)) === index);
	}, [data, idOf]);
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	return {
		runs,
		isLoading,
		error: error ? getApiErrorMessage(error, failure) : null,
		hasMore: hasNextPage,
		loadingMore: isFetchingNextPage,
		loadMore
	};
};
